/* ProtectedQuestion — renders a question with anti-copy protection.
 *
 * ⚠️  IMPORTANT: This component ALREADY wraps `children` in <MathText> internally
 * (see the <MathText>{children}</MathText> inside the <foreignObject> below).
 * DO NOT pass a <MathText> as a child — that creates a nested MathText where
 * the outer one receives a React element instead of a string and ends up
 * rendering empty or "[object Object]". Just pass the raw question string:
 *
 *   ✅ <ProtectedQuestion>{round.question.question}</ProtectedQuestion>
 *   ❌ <ProtectedQuestion><MathText>{round.question.question}</MathText></ProtectedQuestion>
 *
 * Defeats casual "select → copy → paste in Google" cheating by:
 *  (a) blocking selection, copy, cut, drag, and the context menu
 *  (g) rendering the rendered KaTeX/MathText DOM inside an SVG <foreignObject>
 *      so the underlying text is harder to scrape from the page source than
 *      a plain <div>. Combined with `user-select: none` the casual approach
 *      of click-and-drag highlighting is also disabled.
 *
 * Note: this is a *deterrent*, not strong DRM — a determined attacker with
 * dev tools can still extract the text. The goal is to make cheating slow
 * and inconvenient enough that honest play is the path of least resistance.
 */
import React, { useEffect, useRef, useState } from "react";
import MathText from "./MathText";

const block = (e) => { e.preventDefault(); return false; };

export default function ProtectedQuestion({ children, className = "" }) {
  const wrapperRef = useRef(null);
  const innerRef = useRef(null);
  const [dims, setDims] = useState({ width: 320, height: 60 });

  // Size the SVG viewBox to the actual container width so text isn't scaled down
  // on narrow mobile screens (a fixed 1000-unit viewBox shrinks text ~3× on phones).
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const inner = innerRef.current;
    if (!wrapper || !inner) return;

    const update = () => {
      const w = Math.max(1, Math.ceil(wrapper.clientWidth));
      const h = Math.max(1, Math.ceil(inner.scrollHeight) + 4);
      setDims((prev) => (prev.width === w && prev.height === h ? prev : { width: w, height: h }));
    };

    const ro = new ResizeObserver(update);
    ro.observe(wrapper);
    ro.observe(inner);
    update();
    return () => ro.disconnect();
  }, [children]);

  // Hard-block copy/cut/drag at the document level for the protected subtree
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const events = ["copy", "cut", "dragstart", "selectstart"];
    const handlers = events.map((ev) => {
      const fn = (e) => { e.preventDefault(); };
      el.addEventListener(ev, fn);
      return [ev, fn];
    });
    return () => handlers.forEach(([ev, fn]) => el.removeEventListener(ev, fn));
  }, [children]);

  return (
    <div
      ref={wrapperRef}
      className={`anti-copy w-full ${className}`}
      onCopy={block}
      onCut={block}
      onContextMenu={block}
      onDragStart={block}
      data-testid="protected-question"
      style={{
        userSelect: "none",
        WebkitUserSelect: "none",
        MozUserSelect: "none",
        msUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
    >
      <svg
        width="100%"
        height={dims.height}
        viewBox={`0 0 ${dims.width} ${dims.height}`}
        preserveAspectRatio="xMinYMin meet"
        style={{ overflow: "visible", display: "block" }}
        aria-hidden="false"
      >
        <foreignObject x="0" y="0" width={dims.width} height={dims.height} requiredExtensions="http://www.w3.org/1999/xhtml">
          <div
            xmlns="http://www.w3.org/1999/xhtml"
            ref={innerRef}
            className="question-text-inner"
            style={{ pointerEvents: "none", width: "100%" }}
          >
            <MathText>{children}</MathText>
          </div>
        </foreignObject>
      </svg>
    </div>
  );
}
